"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getMessaging, getToken, onMessage, Messaging } from "firebase/messaging";
import { saveFcmToken } from "@/app/api/service/firebase";
import NotificationBlockedDialog from "@/app/components/popup/notification-blocked";
import {
    getUserNotificationPreference,
} from "@/app/utils/notificationpreference";
import {
    getCleverTapServiceWorkerRegistration,
    hasDismissedNotificationBlockedDialogThisSession,
    logPushDebugState,
    markNotificationBlockedDialogDismissedThisSession,
} from "@/app/utils/push-notification-prompt";
import { NotificationPermissionState } from "@/app/enums/notification-permission";

// Firebase config
const firebaseConfig = {
    apiKey: "AIzaSyBEXFmUvsxJBq1Rz5__Dk4FE1PMP4bL5ps",
    authDomain: "incredmoney.firebaseapp.com",
    projectId: "incredmoney",
    storageBucket: "incredmoney.appspot.com",
    messagingSenderId: "150530677387",
    appId: "1:150530677387:web:57f2f870135fbd4300128e",
};

// Utility: Validate VAPID key (URL-safe Base64)
function validateVapidKey(key: string): boolean {
    if (!key) {
        console.error("❌ VAPID key is missing or empty");
        return false;
    }

    try {
        // Convert URL-safe base64 to standard base64
        let paddedKey = key.replace(/-/g, "+").replace(/_/g, "/");
        const pad = paddedKey.length % 4;
        if (pad !== 0) {
            paddedKey += "=".repeat(4 - pad);
        }

        atob(paddedKey); // Will throw if invalid
        return true;
    } catch (e) {
        console.error("❌ Invalid VAPID key format", e);
        return false;
    }
}

let _messaging: Messaging | null = null;

function getFirebaseApp(): FirebaseApp {
    // Prevent duplicate initialization
    if (getApps().length > 0) {
        return getApps()[0];
    }
    return initializeApp(firebaseConfig);
}

function getFirebaseMessaging(): Messaging {
    if (!_messaging) {
        _messaging = getMessaging(getFirebaseApp());
    }
    return _messaging;
}

// Token storage utility
const TOKEN_KEY = "fcm_token";

function storeToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
}

function getStoredToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

// Context Setup
interface FcmTokenContextType {
    token: string | null;
    refreshToken: () => Promise<void>;
}

const FcmTokenContext = createContext<FcmTokenContextType>({
    token: null,
    refreshToken: async () => {},
});

// 🔄 Token API registration
async function triggerTokenRegistration(token: string) {
    try {
        await saveFcmToken(token);
    } catch (error) {
        console.error("Failed to register FCM token:", error);
    }
}

function isIOS(): boolean {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }

    // Check using userAgent only, avoiding deprecated platform property
    return /iPad|iPhone|iPod|Mac|Macintosh|Darwin/i.test(navigator.userAgent);
}

export const FcmTokenProvider = ({ children }: { children: ReactNode }) => {
    const [token, setToken] = useState<string | null>(null);
    const [showNotificationBlocked, setShowNotificationBlocked] = useState(false);

    /** Reset only legacy Firebase messaging SW — never unregister CleverTap push SW. */
    const resetFirebaseMessagingSubscription = async () => {
        if (!("serviceWorker" in navigator)) return;

        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
            const scriptUrl = registration.active?.scriptURL ?? "";
            if (!scriptUrl.includes("firebase-messaging-sw")) continue;

            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
            }
            await registration.unregister();
        }

        if (process.env.NODE_ENV === "development") {
            console.log("FCM: Reset legacy firebase-messaging-sw registration only");
        }
    };

    const refreshToken = async () => {
        try {
            if (typeof window === "undefined") return;

            // Check if path contains /advisory and skip FCM setup if it does
            if (window.location.pathname.includes("/advisory")) {
                console.log("Advisory path detected, skipping FCM token registration");
                return;
            }

            if (Notification.permission !== "granted") {
                console.warn(
                    "Skipping token refresh because notification permission is not granted",
                );
                return; // 🚫 Don't proceed if permission isn't granted
            }

            const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

            if (!vapidKey || !validateVapidKey(vapidKey)) {
                console.error("🚫 Invalid or missing VAPID key");
                setToken(null);
                return;
            }

            const messaging = getFirebaseMessaging();
            const serviceWorkerRegistration = await getCleverTapServiceWorkerRegistration();

            const currentToken = await getToken(messaging, {
                vapidKey,
                ...(serviceWorkerRegistration ? { serviceWorkerRegistration } : {}),
            });

            if (!currentToken) {
                console.log("No registration token available. Request permission to generate one.");
                setToken(null);
                return;
            }

            const storedToken = getStoredToken();
            const isNewToken = !storedToken || storedToken !== currentToken;

            if (isNewToken) {
                console.log("FCM token has changed or is new. Registering new token.");
                await triggerTokenRegistration(currentToken);
                storeToken(currentToken);
            }

            setToken(currentToken);
            void logPushDebugState("fcm:token-refreshed");
        } catch (error: unknown) {
            console.error("Error retrieving FCM token:", error);

            // Define Firebase error type
            interface FirebaseError {
                code?: string;
                name?: string;
            }

            // Handle token-not-registered or push errors by resetting
            if (
                typeof error === "object" &&
                error !== null &&
                (("code" in error &&
                    (error as FirebaseError).code ===
                        "messaging/registration-token-not-registered") ||
                    ("name" in error && (error as FirebaseError).name === "AbortError"))
            ) {
                console.warn(
                    "Token is invalid or PushService failed. Attempting to force reset...",
                );

                try {
                    await resetFirebaseMessagingSubscription();

                    const messaging = getFirebaseMessaging();
                    const serviceWorkerRegistration = await getCleverTapServiceWorkerRegistration();
                    const newToken = await getToken(messaging, {
                        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
                        ...(serviceWorkerRegistration ? { serviceWorkerRegistration } : {}),
                    });

                    if (newToken) {
                        await triggerTokenRegistration(newToken);
                        storeToken(newToken);
                        setToken(newToken);
                        console.log("Recovered new token after reset.");
                    } else {
                        console.log("Still failed to get token after reset.");
                        setToken(null);
                    }
                } catch (resetError) {
                    console.error("Failed to recover after reset:", resetError);
                    setToken(null);
                }
            } else {
                setToken(null);
            }
        }
    };

    useEffect(() => {
        if (typeof window === "undefined") return;

        // First, check if Notification API exists at all
        const notificationSupported = "Notification" in window;

        // 🔍 Check if the app is running inside a WebView
        const isWebView = () => {
            const userAgent = navigator.userAgent || navigator.vendor;
            return /wv|WebView|(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
        };

        if (isWebView() || window?.ReactNativeWebView) {
            console.warn("WebView detected — skipping FCM logic.");
            return;
        }

        if (isIOS()) {
            console.warn("(iOS) Skipping automatic FCM permission flow.");
            return;
        }

        const userPref = getUserNotificationPreference();

        // Only proceed if Notification API is supported
        if (notificationSupported) {
            if (Notification.permission === NotificationPermissionState.GRANTED) {
                refreshToken();

                // ✅ Safe to initialize messaging and listen to foreground messages
                const messaging = getFirebaseMessaging();
                const unsubscribe = onMessage(messaging, (payload) => {
                    console.log("Message received in foreground:", payload);
                });

                return () => {
                    unsubscribe();
                };
            }

            if (
                userPref === NotificationPermissionState.DENIED &&
                Notification.permission === NotificationPermissionState.DENIED &&
                !hasDismissedNotificationBlockedDialogThisSession()
            ) {
                setShowNotificationBlocked(true);
            }
        } else {
            console.warn("Notification API not supported in this environment");
            // You could implement an alternative notification system here
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || !("Notification" in window)) return;

        const tryRefreshOnPermissionGrant = async () => {
            if (Notification.permission === NotificationPermissionState.GRANTED) {
                setShowNotificationBlocked(false);
                await refreshToken();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === NotificationPermissionState.VISIBLE) {
                void tryRefreshOnPermissionGrant();
            }
        };

        window.addEventListener("focus", tryRefreshOnPermissionGrant);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("focus", tryRefreshOnPermissionGrant);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    return (
        <FcmTokenContext.Provider value={{ token, refreshToken }}>
            {children}
            <NotificationBlockedDialog
                open={showNotificationBlocked}
                onAccept={() => {
                    markNotificationBlockedDialogDismissedThisSession();
                    setShowNotificationBlocked(false);
                }}
            />
        </FcmTokenContext.Provider>
    );
};

export const useFcmToken = () => useContext(FcmTokenContext);